/**
 * Modal for starting a new direct message conversation.
 */

import React, { useState } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Input,
  List,
  ListItem,
  ListItemButton,
  ListItemDecorator,
  ListItemContent,
  Avatar,
  Box,
  CircularProgress,
  Alert,
} from '@mui/joy';
import { MagnifyingGlass as SearchIcon, User as PersonIcon } from '@phosphor-icons/react';
import { useOrganizationUsers } from '../../hooks/useOrganizationUsers';
import { useStartDM } from '../../hooks/useChat';

interface NewDMModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (channelId: string) => void;
  currentUserId?: string | number;
}

const NewDMModal: React.FC<NewDMModalProps> = ({
  open,
  onClose,
  onSuccess,
  currentUserId,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const { users, loading: usersLoading } = useOrganizationUsers();
  const startDM = useStartDM();

  // Filter users by search query and exclude current user
  const filteredUsers = users.filter((user) => {
    if (currentUserId && String(user.id) === String(currentUserId)) {
      return false;
    }
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.full_name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.first_name?.toLowerCase().includes(query) ||
      user.last_name?.toLowerCase().includes(query)
    );
  });

  const handleSelectUser = async (userId: number) => {
    setError(null);
    setSelectedUserId(String(userId));

    try {
      const result = await startDM.mutateAsync({
        user_id: String(userId),
      });

      // Reset and close
      setSearchQuery('');
      setSelectedUserId(null);
      onClose();
      onSuccess?.(result.id);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start conversation';
      setError(errorMessage);
      setSelectedUserId(null);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setError(null);
    setSelectedUserId(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ minWidth: 400, maxHeight: '80vh', overflow: 'hidden' }}>
        <ModalClose />
        <Typography level="h4" sx={{ mb: 2 }}>
          New Direct Message
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Input
            placeholder="Search users..."
            startDecorator={<SearchIcon />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
        </Box>

        {error && (
          <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ overflow: 'auto', maxHeight: 300 }}>
          {usersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size="sm" />
            </Box>
          ) : filteredUsers.length === 0 ? (
            <Typography level="body-sm" sx={{ textAlign: 'center', color: 'text.secondary', p: 3 }}>
              {searchQuery ? 'No users found' : 'No users available'}
            </Typography>
          ) : (
            <List>
              {filteredUsers.map((user) => (
                <ListItem key={user.id}>
                  <ListItemButton
                    onClick={() => handleSelectUser(user.id)}
                    disabled={startDM.isPending && selectedUserId === String(user.id)}
                  >
                    <ListItemDecorator>
                      <Avatar size="sm">
                        {user.first_name?.[0] || user.email?.[0] || <PersonIcon />}
                      </Avatar>
                    </ListItemDecorator>
                    <ListItemContent>
                      <Typography level="body-sm" fontWeight="md">
                        {user.full_name || `${user.first_name} ${user.last_name}`.trim() || user.email}
                      </Typography>
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        {user.email}
                      </Typography>
                    </ListItemContent>
                    {startDM.isPending && selectedUserId === String(user.id) && (
                      <CircularProgress size="sm" />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </ModalDialog>
    </Modal>
  );
};

export default NewDMModal;
